import { FC, useCallback, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { TReferralsPartnersRow } from 'redux/reducers/referrals/types';
import { useDispatch, useSelector } from 'react-redux';
import {
	getReferralsPartners,
	getReferralsPartnersIsLoad,
} from 'redux/reducers/referrals/selectors';
import { getReferralsPartnersRequest } from 'redux/reducers/referrals/reducer';
import { PAGE_SIZE } from 'services/constants/env';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { TFilters, TOrderBy } from 'redux/types';
import { DATES_ARRAY } from 'services/constants/dates-constants';
import { getUsersList } from 'redux/reducers/user/selectors';
import { TTableStructure } from 'ui/EasTable/types';
import { TEMManagementRow } from 'components/EMManagement/types';
import { tableStructureFunction } from './tableStructure';
import { ReferralsTabs } from './ReferralsTabs';
import { AddReferralDialog } from './AddReferralDialog';

export const Referrals: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);
	const referralsPartners = useSelector(getReferralsPartners);
	const referralsPartnersIsLoading = useSelector(getReferralsPartnersIsLoad);
	const usersList = useSelector(getUsersList) ?? [];
	const tableStructure = tableStructureFunction({ usersList });
	const [currentTableStructure, setCurrentTableStructure] = useState(tableStructure);
	const navigateParamsWithoutPage = tableStructure.reduce((acc, item) => {
		if (!!navigateParams[item.selector] && item.type === 'date') {
			const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector]);
			// console.log('dates', dates);
			if (dates?.from) {
				acc.push({
					fieldName: item.selector,
					value: dates.from,
					operator: '>=',
				});
			}
			if (dates?.to) {
				acc.push({
					fieldName: item.selector,
					value: dates.to,
					operator: '<=',
				});
			}
			return acc;
		}
		if (navigateParams[item.selector])
			acc.push({
				fieldName: item.selector,
				value: navigateParams[item.selector] as string,
				operator: '=',
			});
		return acc;
	}, [] as TFilters);

	useEffect(() => {
		const orderBy = [];
		if (navigateParams?.sortIndex) {
			orderBy.push({
				fieldName: navigateParams?.sortIndex
					? tableStructure[+navigateParams.sortIndex].selector
					: 'id',
				direction: navigateParams?.sortDirection
					? String(navigateParams.sortDirection) === 'desc'
						? 'desc'
						: 'asc'
					: 'desc',
			} as TOrderBy);
		}
		orderBy.push({
			fieldName: 'id',
			direction: 'desc',
		} as TOrderBy);
		dispatch(
			getReferralsPartnersRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(navigateParams?.search ?? ''),
				filters: navigateParamsWithoutPage,
				orderBy,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TReferralsPartnersRow>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const [openAddReferralDialog, setOpenAddReferralDialog] = useState(false);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<ReferralsTabs />
			<EasTableWithFilter<TReferralsPartnersRow>
				tableStructure={currentTableStructure}
				items={referralsPartners}
				itemsIsLoading={referralsPartnersIsLoading}
				handleAction={() => {}}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
					<Typography variant="body1" color="primary">
						Extra Services / Referral Hub / <b>Referrals</b>
					</Typography>
					<Button
						variant="contained"
						color="primary"
						size="small"
						onClick={() => setOpenAddReferralDialog(true)}
					>
						Add Referral
					</Button>
				</Box>
			</EasTableWithFilter>
			<AddReferralDialog
				open={openAddReferralDialog}
				onClose={() => setOpenAddReferralDialog(false)}
				title="Add Referral"
				updatePageRequestData={{
					pageNumber: page ? +page : 1,
					pageSize: PAGE_SIZE,
					searchString: String(navigateParams?.search ?? ''),
					filters: navigateParamsWithoutPage,
				}}
			/>
		</Box>
	);
};
